import 'intersection-observer';
import { useEffect, useState } from 'react';
import { animateScroll } from 'react-scroll';
import isTouchDevice from 'is-touch-device';
import { MouseParallaxContainer, MouseParallaxChild } from 'react-parallax-mouse';
import { ParallaxProvider, Parallax } from 'react-scroll-parallax';
import { Context, HashLocation } from '@blakeembrey/react-location';
import classnames from 'classnames';
import ScrollLock from 'react-scrolllock';

import Header from 'components/header';
import PageLoading from 'pages/loading';
import PageLanding from 'pages/landing';
import PageArtwork from 'pages/artwork';
import { ArtworkMapItem } from 'pages/artwork/components/artwork/ArtworkMap.jsx';

import styles from './App.module.scss';

import ReactGA from 'react-ga4';
ReactGA.initialize('G-YL17HN9D8Y');

function getArtwork(pathname) {
  if (pathname.indexOf('/artwork/') === 0) {
    let artwork = pathname.slice('/artwork/'.length);
    switch (artwork) {
      case 'innovationpin':
      case 'interview':
      case 'franknitty':
      case 'ericbot404':
      case 'keithlam':
      case 'stephlau':
      case 'yarliallison':
      case 'argallery':
        break;
      default:
        if (artwork.startsWith('interview-')) {
          artwork = 'interview';
        } else {
          artwork = 'innovationpin';
        }
        break;
    }
    return artwork;
  }
  return null;
}

function App() {
  const hashLocation = new HashLocation();
  const [page, setPage] = useState(hashLocation.url.pathname);
  const [prevPage, setPrevPage] = useState(null);
  const [loading, setLoading] = useState(0);
  const [height, setHeight] = useState(1280);
  const [backdropActive, setBackdropActive] = useState(false);
  const [activeArtworkMapItem, setActiveArtworkMapItem] = useState(null);
  const [nextActiveArtworkMapItem, setNextActiveArtworkMapItem] = useState(null);
  const setArtworkMapItem = (artwork) => {
    if (activeArtworkMapItem) {
      setNextActiveArtworkMapItem(null);
      setBackdropActive(false);
    } else if (artwork) {
      setNextActiveArtworkMapItem(artwork);
      setBackdropActive(true);
    }
  };

  useEffect(() => {
    if (backdropActive && nextActiveArtworkMapItem) {
      setActiveArtworkMapItem(nextActiveArtworkMapItem);
      setNextActiveArtworkMapItem(null);
    } else if (!backdropActive && !nextActiveArtworkMapItem) {
      setTimeout(() => {
        setActiveArtworkMapItem(null);
      }, 750);
    }
  }, [backdropActive, nextActiveArtworkMapItem]);

  useEffect(() => {
    if (window.pymChild) {
      window.pymChild.onMessage('viewport-iframe-position', function(parentInfo) {
        parentInfo = parentInfo.split(' ');
        if (parentInfo[1] && parseInt(parentInfo[1]) !== undefined) {
          setHeight(parseInt(parentInfo[1]));
        }
      });
      window.pymChild.getParentPositionInfo();

      let parentLocationHash = window.pymChild.parentUrl ? window.pymChild.parentUrl.split('#') : null;
      if (parentLocationHash && parentLocationHash[1]) {
        parentLocationHash = parentLocationHash[1];

        switch (parentLocationHash) {
          case 'innovationpin':
          case 'ericbot404':
          case 'franknitty':
          case 'keithlam':
          case 'stephlau':
          case 'yarliallison':
          case 'argallery':
            hashLocation.push(`/artwork/${parentLocationHash}`);
            break;
          case 'artistinterview':
            hashLocation.push('/artwork/interview');
            break;
          default:
            if (parentLocationHash.startsWith('interview-')) {
              hashLocation.push('/artwork/interview');
            }
            break;
        }
      }
    }
    setTimeout(() => {
      setLoading(0.5);
    }, 200);
    setTimeout(() => {
      setLoading(1);
    }, 2000);
  }, []); // eslint-disable-line

  useEffect(() => {
    return hashLocation.onChange((url) => {
      const nextPage = url.pathname;
      if (nextPage === page) {
        return;
      }
      setPrevPage(page);
      setPage(nextPage);

      setTimeout(() => {
        setPrevPage(null);
      }, 1000);

      animateScroll.scrollToTop();

      ReactGA.send('pageview');
    });
  }, [page]); //eslint-disable-line

  let pageProps = {
    page,
    setPage: (nextPage) => {
      if (nextPage === page) {
        return;
      }
      setPrevPage(page);
      setPage(nextPage);
      hashLocation.push(nextPage);

      setTimeout(() => {
        setPrevPage(null);
      }, 1000);

      if (nextPage.startsWith('/artwork/interview-')) {
        animateScroll.scrollToBottom();
      } else {
        animateScroll.scrollToTop();
      }

      if (nextPage === '/' || (nextPage.indexOf('/artwork/') === 0 && page === '/')) {
        window.pymChild && window.pymChild.scrollParentToChildPos(-100);
      }
    },
    loading,
    setLoading,
  };

  const getClassName = (_page) => {
    let tmp = [];
    if (
      (page === '/' && _page === 'landing') ||
      (page.indexOf('/artwork/') === 0 && _page === 'artwork')
    ) {
      tmp.push(styles.visible);
      tmp.push(styles.displayBlock);
    } else if (
      prevPage && (
        (prevPage === '/' && _page === 'landing') ||
        (prevPage.indexOf('/artwork/') === 0 && _page === 'artwork')
      )
    ) {
      tmp.push(styles.displayBlock);
      tmp.push(styles.prevPage);
    }

    return classnames(
      styles.page,
      styles[_page],
      ...tmp
    );
  };

  return (
    <Context.Provider value={ hashLocation }>
      <div className={ styles.bg } style={{ backgroundImage: `url('${process.env.PUBLIC_URL}/images/bg.jpg')` }} />
      <div className={ classnames(styles.cloudBgContainer, page === '/artwork/argallery' && styles.cloudBgContainerHide) }>
        {
          isTouchDevice() ? (
            <ParallaxProvider>
              <div className={ styles.parallaxContainer }>
                <Parallax
                  translateX={[-20, 30]}
                  translateY={[-30, 20]}
                  className={ styles.cloudBg1 }
                >
                  <div
                    className={ styles.cloudBg }
                    style={{ backgroundImage: `url('${process.env.PUBLIC_URL}/images/cloud-bg.png')` }}
                  />
                </Parallax>
                <Parallax
                  translateX={[0, -20]}
                  translateY={[10, -10]}
                  className={ styles.cloudBg2 }
                >
                  <div
                    className={ styles.cloudBg }
                    style={{ backgroundImage: `url('${process.env.PUBLIC_URL}/images/cloud-bg.png')` }}
                  />
                </Parallax>
              </div>
            </ParallaxProvider>
          ) : (
            <MouseParallaxContainer useWindowMouseEvents className={ styles.parallaxContainer }>
              <MouseParallaxChild
                factorX={ 0.03 }
                factorY={ 0.05 }
                className={ styles.cloudBg1 }
              >
                <div
                  className={ styles.cloudBg }
                  style={{ backgroundImage: `url('${process.env.PUBLIC_URL}/images/cloud-bg.png')` }}
                />
              </MouseParallaxChild>
              <MouseParallaxChild
                factorX={ 0.07 }
                factorY={ 0.08 }
                className={ styles.cloudBg2 }
              >
                <div
                  className={ styles.cloudBg }
                  style={{ backgroundImage: `url('${process.env.PUBLIC_URL}/images/cloud-bg.png')` }}
                />
              </MouseParallaxChild>
            </MouseParallaxContainer>
          )
        }
      </div>
      <Header page={ page } setPage={ pageProps.setPage } />
      <PageLanding
        className={ getClassName('landing') }
        {...pageProps}
      />
      <PageArtwork
        className={ getClassName('artwork') }
        artwork={ getArtwork(page) }
        currentArtworkMapItem={ backdropActive && activeArtworkMapItem }
        setArtworkMapItem={ setArtworkMapItem }
        {...pageProps}
      />
      <PageLoading
        className={ classnames(styles.loading, loading < 1 && styles.show) }
        loading={ loading }
        height={ height }
      />

      <div className={ classnames(styles.artworkMapPopup, (activeArtworkMapItem || nextActiveArtworkMapItem) && styles.show) }>
        <div className={ classnames(styles.backdrop, backdropActive && styles.active) } onClick={ () => setArtworkMapItem(null) } />
        {
          (activeArtworkMapItem || nextActiveArtworkMapItem) && (
            <div className={ classnames(styles.artworkMapItem, styles[activeArtworkMapItem || nextActiveArtworkMapItem]) }>
              <ArtworkMapItem
                artwork={ activeArtworkMapItem || nextActiveArtworkMapItem }
                current={ backdropActive && activeArtworkMapItem }
                onClick={ () => setArtworkMapItem(null) }
              />
            </div>
          )
        }
      </div>

      <ScrollLock isActive={ loading < 1 || !!activeArtworkMapItem } />
    </Context.Provider>
  );
}

export default App;